export interface PiiSpan {
  /** Start character index in the clean (marker-stripped) text */
  start: number;
  /** End character index in the clean text */
  end: number;
  /** PII type, e.g. "PERSON_NAME", "DATE_OF_BIRTH" */
  type: string;
}

const PII_MARKER_REGEX = /\{\{PII:([A-Z_]+)\}\}([\s\S]*?)\{\{\/P(?:II|PI)(?::[A-Z_]+)?\}\}/g;

/**
 * Parse {{PII:TYPE}}content{{/PII}} markers from synthesis output.
 *
 * Returns the text with marker syntax stripped (preserving the inner PII content)
 * and an array of character-offset spans in the clean text.
 *
 * When no markers are present (e.g. old data), cleanText === text and spans is empty.
 * Malformed/unclosed markers pass through as literal text.
 */
export function parsePiiMarkers(text: string): { cleanText: string; spans: PiiSpan[] } {
  // Fast path: no markers at all
  if (!text || !text.includes('{{PII:')) {
    return { cleanText: text || '', spans: [] };
  }

  const spans: PiiSpan[] = [];
  let cleanText = '';
  let lastIndex = 0;

  // Reset regex state
  PII_MARKER_REGEX.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = PII_MARKER_REGEX.exec(text)) !== null) {
    const [fullMatch, type, innerContent] = match;
    const matchStart = match.index;

    // Append text before this marker
    cleanText += text.slice(lastIndex, matchStart);

    // Record span position in clean text
    const spanStart = cleanText.length;
    cleanText += innerContent;
    const spanEnd = cleanText.length;

    if (spanEnd > spanStart) {
      spans.push({ start: spanStart, end: spanEnd, type });
    }

    lastIndex = matchStart + fullMatch.length;
  }

  // Append remaining text after last marker
  cleanText += text.slice(lastIndex);

  return { cleanText, spans };
}
